import { useWatch } from 'react-hook-form';

import {
	createLog,
	LogSeverity,
	paymentConfigDtoSchema,
	type PaymentConfigOutputDto,
	PROGRAM_SECTIONS,
	type ProgramOwnerServiceConfig,
	Services,
	type ServicesProvider,
	SUBSECTIONS,
} from '@ekhodealer/ekho-common';
import { Box, Form, Stack, TitleGroup } from '@ekhodealer/ekho-common/rev';

import { SettingsSection } from '@common/components/SettingsSection';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateCheckoutPayment } from '../../../../api/configuration/services/useUpdateCheckoutPayment';
import { useServiceProgramInfo } from '../../../../hooks/useServiceProgramInfo';

// ---------------------------------------------------------------------------
// Per-box schemas
// ---------------------------------------------------------------------------
// Each gray box is its own Form so that Save/Clear buttons appear inside
// the box (aligned with the paradigm used on Business Details, Bank Account,
// and AI BDC settings pages).
//
// The backend endpoint (POST /program/checkout/payment) expects the full
// paymentConfig payload, so each sub-form merges its changed fields with the
// current SP data before submitting. This is safe because the backend uses
// Firestore dot-notation merges (buildFieldPathUpdates) — only the changed
// leaf fields are actually written.
// ---------------------------------------------------------------------------

const reserveSchema = paymentConfigDtoSchema.pick({
	STANDARD_RESERVATION: true,
	LATE_REMAINING_BALANCE: true,
});

const financingSchema = paymentConfigDtoSchema.pick({
	LOAN_DOWNPAYMENT: true,
	LOAN_ADDITIONAL_DOWN: true,
});

const payInFullSchema = paymentConfigDtoSchema.pick({
	CHECKOUT_FULL_PAYMENT: true,
	ACH_CHECKOUT_FULL_PAYMENT: true,
});

const klarnaSchema = paymentConfigDtoSchema.pick({
	KLARNA_CHECKOUT_FULL_PAYMENT: true,
});

const inPersonSchema = paymentConfigDtoSchema.pick({
	IN_PERSON_PAYMENT: true,
});

/**
 * Read the current full payment config from the services provider.
 * Always includes EARLY_REMAINING_BALANCE (hidden, always-on).
 */
function getCurrentPaymentConfig(
	servicesProvider: ServicesProvider | undefined | null,
): PaymentConfigOutputDto {
	const programOwnerConfig = servicesProvider?.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;
	const paymentConfig = programOwnerConfig?.checkout?.paymentConfig ?? {};

	return {
		...paymentConfig,
		EARLY_REMAINING_BALANCE: paymentConfig.EARLY_REMAINING_BALANCE ?? {
			paymentTypeEnum: 'EARLY_REMAINING_BALANCE',
		},
	} as PaymentConfigOutputDto;
}

/**
 * Build the full payment config payload by merging changed fields with
 * current SP data. Converts undefined payment types to null so the backend
 * doesn't accidentally delete them.
 */
function buildPaymentPayload(
	current: PaymentConfigOutputDto,
	overrides: Partial<PaymentConfigOutputDto>,
): PaymentConfigOutputDto {
	const merged = { ...current, ...overrides };

	// Convert undefined values to null (except EARLY_REMAINING_BALANCE)
	// so the backend knows the field exists but is intentionally unset
	const processed = { ...merged } as Record<string, unknown>;
	for (const key in processed) {
		if (key !== 'EARLY_REMAINING_BALANCE' && processed[key] === undefined) {
			processed[key] = null;
		}
	}

	return processed as PaymentConfigOutputDto;
}

export function CheckoutPaymentForm(): React.ReactNode {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(
		PROGRAM_SECTIONS.CHECKOUT,
		SUBSECTIONS.CHECKOUT_PAYMENT,
	);
	const updateCheckoutPayment = useUpdateCheckoutPayment();

	if (!programInfo.shouldRender) {
		return null;
	}

	// TODO: Make dynamic after implementing authZ
	const isReadOnly = true;

	const current = getCurrentPaymentConfig(servicesProvider);

	// Each handler merges its fields with the current config and submits the full payload
	const createSubmitHandler =
		(keys: (keyof PaymentConfigOutputDto)[]) =>
		async (data: Partial<PaymentConfigOutputDto>): Promise<{ isSuccess: boolean }> => {
			try {
				const overrides: Partial<PaymentConfigOutputDto> = {};
				for (const key of keys) {
					overrides[key] = (data as Record<string, unknown>)[key] as never;
				}
				const payload = buildPaymentPayload(current, overrides);
				await updateCheckoutPayment.mutateAsync(payload);
				return { isSuccess: true };
			} catch (error) {
				createLog(
					'config-mgmt',
					`Failed to update payment config: ${error instanceof Error ? error.message : String(error)}`,
					LogSeverity.ERROR,
				);
				return { isSuccess: false };
			}
		};

	return (
		<SettingsSection
			title='Payment methods'
			description='Control which payment options buyers see at checkout and in the buyer portal. These can be further customized per vehicle.'>
			<Stack itemsSpacing='16'>
				{/* Reserve with deposit */}
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-payment-reserve'
						schema={reserveSchema}
						defaultValues={{
							STANDARD_RESERVATION: current.STANDARD_RESERVATION,
							LATE_REMAINING_BALANCE: current.LATE_REMAINING_BALANCE,
						}}
						onSubmit={createSubmitHandler(['STANDARD_RESERVATION', 'LATE_REMAINING_BALANCE'])}
						showSaveAndClear>
						<ReserveFields isReadOnly={isReadOnly} />
					</Form>
				</Box>

				{/* Financing */}
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-payment-financing'
						schema={financingSchema}
						defaultValues={{
							LOAN_DOWNPAYMENT: current.LOAN_DOWNPAYMENT,
							LOAN_ADDITIONAL_DOWN: current.LOAN_ADDITIONAL_DOWN,
						}}
						onSubmit={createSubmitHandler(['LOAN_DOWNPAYMENT', 'LOAN_ADDITIONAL_DOWN'])}
						showSaveAndClear>
						<FinancingFields isReadOnly={isReadOnly} />
					</Form>
				</Box>

				{/* Pay in full */}
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-payment-full'
						schema={payInFullSchema}
						defaultValues={{
							CHECKOUT_FULL_PAYMENT: current.CHECKOUT_FULL_PAYMENT,
							ACH_CHECKOUT_FULL_PAYMENT: current.ACH_CHECKOUT_FULL_PAYMENT,
						}}
						onSubmit={createSubmitHandler(['CHECKOUT_FULL_PAYMENT', 'ACH_CHECKOUT_FULL_PAYMENT'])}
						showSaveAndClear>
						<PayInFullFields isReadOnly={isReadOnly} />
					</Form>
				</Box>

				{/* Buy now, pay later (Klarna) */}
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-payment-klarna'
						schema={klarnaSchema}
						defaultValues={{
							KLARNA_CHECKOUT_FULL_PAYMENT: current.KLARNA_CHECKOUT_FULL_PAYMENT,
						}}
						onSubmit={createSubmitHandler(['KLARNA_CHECKOUT_FULL_PAYMENT'])}
						showSaveAndClear>
						<Form.Field.Switch
							schemaField='KLARNA_CHECKOUT_FULL_PAYMENT'
							labelText='Buy now, pay later (Klarna)'
							descriptionText='Allow buyers to finance their purchase using Klarna. Available for cart totals between $5,000-$10,000. Note: you cover the 5% transaction fee.'
							togglePosition='right'
							level='subgroup'
							titleWeight='semibold'
							objectValue={{ paymentTypeEnum: 'KLARNA_CHECKOUT_FULL_PAYMENT' }}
							isDisabled={isReadOnly}
						/>
					</Form>
				</Box>

				{/* Pay in person */}
				<Box padding='24' background='secondary' rounding='12'>
					<Form
						name='checkout-payment-in-person'
						schema={inPersonSchema}
						defaultValues={{
							IN_PERSON_PAYMENT: current.IN_PERSON_PAYMENT,
						}}
						onSubmit={createSubmitHandler(['IN_PERSON_PAYMENT'])}
						showSaveAndClear>
						<Form.Field.Switch
							schemaField='IN_PERSON_PAYMENT'
							labelText='Pay in person'
							descriptionText='Allow buyers to complete their purchase in person at your dealership. No online payment is collected at checkout. Instead, the buyer information is saved as a lead and routed to the appropriate lead pipeline.'
							togglePosition='right'
							level='subgroup'
							titleWeight='semibold'
							objectValue={{ paymentTypeEnum: 'IN_PERSON_PAYMENT' }}
							isDisabled={isReadOnly}
						/>
					</Form>
				</Box>
			</Stack>
		</SettingsSection>
	);
}

// ---------------------------------------------------------------------------
// Sub-form field components (must be inside a Form to use useWatch)
// ---------------------------------------------------------------------------

function ReserveFields({ isReadOnly }: { isReadOnly: boolean }): React.ReactNode {
	const standardReservation = useWatch({ name: 'STANDARD_RESERVATION' });

	return (
		<Stack itemsSpacing='16'>
			<Form.Field.Switch
				schemaField='STANDARD_RESERVATION'
				labelText='Reserve with deposit'
				descriptionText='Allow buyers to pay a deposit to reserve the vehicle, then pay the remaining balance before fulfillment. Best for vehicles that are not yet available or are built to order.'
				togglePosition='right'
				level='subgroup'
				titleWeight='semibold'
				objectValue={{ paymentTypeEnum: 'STANDARD_RESERVATION' }}
				isDisabled={isReadOnly}
			/>
			{standardReservation && (
				<Box background='primary' padding='16'>
					<Form.Field.Switch
						schemaField='LATE_REMAINING_BALANCE'
						labelText='Remaining balance payment'
						descriptionText='Allow buyers who reserved with a deposit to defer paying the remaining balance until closer to their delivery or pickup date. When turned off, buyers must pay the full remaining balance immediately after reserving — this encourages faster payment and reduces the risk of late cancellations.'
						togglePosition='left'
						level='minor'
						objectValue={{ paymentTypeEnum: 'LATE_REMAINING_BALANCE' }}
						isDisabled={isReadOnly}
					/>
				</Box>
			)}
		</Stack>
	);
}

function FinancingFields({ isReadOnly }: { isReadOnly: boolean }): React.ReactNode {
	const loanDownpayment = useWatch({ name: 'LOAN_DOWNPAYMENT' });
	const loanAdditionalDown = useWatch({ name: 'LOAN_ADDITIONAL_DOWN' });

	return (
		<Stack itemsSpacing='16'>
			<Form.Field.Switch
				schemaField='LOAN_DOWNPAYMENT'
				labelText='Financing'
				descriptionText='Allow buyers to apply for financing through lending partners at checkout. Please confirm with your Ekho account manager which financing programs are active for you.'
				togglePosition='right'
				level='subgroup'
				titleWeight='semibold'
				objectValue={{ paymentTypeEnum: 'LOAN_DOWNPAYMENT' }}
				isDisabled={isReadOnly}
			/>
			{loanDownpayment && (
				<Stack itemsSpacing='16'>
					<Form.Field.Switch
						schemaField='LOAN_ADDITIONAL_DOWN'
						labelText='Minimum down payment required'
						togglePosition='left'
						level='minor'
						objectValue={() => ({
							paymentTypeEnum: 'LOAN_ADDITIONAL_DOWN',
							minAdditionalDown: 0,
						})}
						isDisabled={isReadOnly}
					/>
					{loanAdditionalDown && (
						<Form.Field.Text
							schemaField='LOAN_ADDITIONAL_DOWN.minAdditionalDown'
							labelText='Minimum down payment'
							descriptionText='The minimum down payment required from the buyer when financing.'
							required
							type='number'
							placeholder='0'
							isDisabled={isReadOnly}
						/>
					)}
				</Stack>
			)}
		</Stack>
	);
}

function PayInFullFields({ isReadOnly }: { isReadOnly: boolean }): React.ReactNode {
	const checkoutFullPayment = useWatch({ name: 'CHECKOUT_FULL_PAYMENT' });
	const achCheckoutFullPayment = useWatch({ name: 'ACH_CHECKOUT_FULL_PAYMENT' });

	return (
		<Stack itemsSpacing='16'>
			<TitleGroup
				title='Pay in full'
				description='Allow buyers to pay the full vehicle price at checkout. You may optionally offer a discount to encourage full payment.'
				level='subgroup'
				titleWeight='semibold'
			/>
			<Form.Field.Switch
				schemaField='CHECKOUT_FULL_PAYMENT'
				labelText='Credit card'
				descriptionText='Buyer pays the full amount via credit card at checkout. Note: you will cover processing fees (~3.0%).'
				togglePosition='left'
				level='minor'
				objectValue={{ paymentTypeEnum: 'CHECKOUT_FULL_PAYMENT' }}
				isDisabled={isReadOnly}
			/>
			<Form.Field.Switch
				schemaField='ACH_CHECKOUT_FULL_PAYMENT'
				labelText='Instant bank transfer'
				descriptionText='Buyer pays the full amount via instant bank transfer at checkout. You will cover processing fees (usually 0.75–0.85%). * In most cases, bank transfers are instant. In some situations, transfer may take 1–2 business days.'
				togglePosition='left'
				level='subgroup'
				objectValue={{ paymentTypeEnum: 'ACH_CHECKOUT_FULL_PAYMENT' }}
				isDisabled={isReadOnly}
			/>
			{(checkoutFullPayment || achCheckoutFullPayment) && (
				<Box background='primary' padding='16'>
					<Form.Field.Text
						schemaField='CHECKOUT_FULL_PAYMENT.fullPaymentDiscountPerc'
						labelText='Apply full payment discount'
						descriptionText='Percentage discount given to buyers who pay in full.'
						type='percentage'
						placeholder='0'
						showFieldToggle
						isDisabled={isReadOnly}
					/>
					<div style={{ display: 'none' }}>
						<Form.Field.Text
							schemaField='ACH_CHECKOUT_FULL_PAYMENT.fullPaymentDiscountPerc'
							labelText=''
							descriptionText=''
							type='percentage'
							placeholder='0'
							linkedField='CHECKOUT_FULL_PAYMENT.fullPaymentDiscountPerc'
							isDisabled={isReadOnly}
						/>
					</div>
				</Box>
			)}
		</Stack>
	);
}

export default CheckoutPaymentForm;
